import { useId } from 'react';
import { Activity, ArrowUpRight, CircleAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { DashboardStats } from '../api/dashboard.service';

const COLORS = ['#581c87', '#10b981', '#f59e0b', '#64748b', '#a855f7', '#ec4899'];

export function DashboardAnalytics({ stats, loading }: { stats: DashboardStats | null; loading: boolean }) {
  const chartId = useId();
  if (!stats) return <section aria-label="Indicadores da carteira" aria-busy={loading} className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
    {loading ? <div role="status" className="h-44 animate-pulse rounded-lg bg-slate-100">Carregando indicadores...</div> : <p className="text-sm text-gray-500">Indicadores indisponíveis.</p>}
  </section>;
  const totalActive = stats.plans.reduce((total, row) => total + row.count, 0);
  let offset = 0;
  const segments = stats.plans.map((plan, index) => {
    const percentage = totalActive ? plan.count / totalActive * 100 : 0;
    const segment = { ...plan, percentage, offset, color: COLORS[index % COLORS.length] };
    offset += percentage;
    return segment;
  });
  return <>
    <div className="grid gap-6 lg:grid-cols-2">
      <section aria-label="Distribuição por plano" className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-900">Distribuição por plano</h2>
        <p className="mt-1 text-xs text-slate-500">Assinaturas ativas por plano</p>
        {!totalActive ? <p className="py-12 text-center text-sm text-slate-500">Nenhuma assinatura ativa.</p> :
          <div className="mt-5 flex flex-col items-center gap-6 sm:flex-row">
            <svg viewBox="0 0 120 120" role="img" aria-labelledby={chartId} className="h-44 w-44 shrink-0">
              <title id={chartId}>{segments.map((plan) => plan.name + ': ' + plan.count).join(', ')}</title>
              <circle cx="60" cy="60" r="44" fill="none" stroke="#f1f5f9" strokeWidth="15" />
              {segments.map((plan) => <circle key={plan.planId} cx="60" cy="60" r="44" fill="none" stroke={plan.color} strokeWidth="15" pathLength="100" strokeDasharray={`${plan.percentage} ${100 - plan.percentage}`} strokeDashoffset={-plan.offset} transform="rotate(-90 60 60)" />)}
              <text x="60" y="58" textAnchor="middle" className="fill-slate-900 text-xl font-semibold">{totalActive}</text>
              <text x="60" y="73" textAnchor="middle" className="fill-slate-500 text-[8px]">ativas</text>
            </svg>
            <ul className="w-full space-y-3">{segments.map((plan) => <li key={plan.planId} className="flex items-center gap-2 text-sm">
              <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: plan.color }} />
              <span className="min-w-0 flex-1 truncate">{plan.name}</span>
              <span className="tabular-nums text-slate-500">{plan.count} · {Math.round(plan.percentage)}%</span>
            </li>)}</ul>
          </div>}
      </section>
      <section aria-label="Situação da carteira" className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Activity size={16} className="text-primary" />Situação da carteira</h2>
        <dl className="mt-5 space-y-4 text-sm">
          {[['Em período de teste', stats.trialingSubscriptions, 'text-slate-700'], ['Aguardando pagamento', stats.pendingSubscriptions, 'text-amber-700'], ['Assinaturas atrasadas', stats.overdueSubscriptions, 'text-red-600'], ['Assinaturas canceladas', stats.canceledSubscriptions, 'text-slate-500']].map(([label, count, color]) => <div key={label} className="flex items-center justify-between border-b border-slate-100 pb-3"><dt className="text-slate-500">{label}</dt><dd className={`font-semibold tabular-nums ${color}`}>{count}</dd></div>)}
        </dl>
        <div className="mt-4 flex items-center justify-between gap-3 text-xs">
          <p className="flex items-center gap-1 text-slate-500"><CircleAlert size={14} />Situação atual, não histórico de pagamentos.</p>
          <Link to="/companies" className="inline-flex shrink-0 items-center gap-1 font-medium text-primary">Ver empresas<ArrowUpRight size={14} /></Link>
        </div>
      </section>
    </div>
  </>;
}
